/**
 * The platform default country calling code, used when the tenant setting is missing or unusable.
 *
 * Mirrors backend `PhoneNumberSettingsProvider.DefaultCountryCodeValue`
 * (`Domain/Settings/PhoneNumberSettingsProvider.cs`) — the single place the frontend may name a
 * country code as a default. Everything else receives the tenant's code as a parameter.
 */
export const PLATFORM_DEFAULT_COUNTRY_CODE = '90';

/**
 * Shape of a country calling code: one to three digits, the first not zero. The backend checks the
 * value against its full ITU code list (`CountryCallingCodes.All`); the frontend has no copy of that
 * list, so it only checks the shape.
 */
const COUNTRY_CODE_SHAPE = /^[1-9]\d{0,2}$/;

/**
 * Resolves the stored `PhoneNumber.DefaultCountryCode` setting the way backend
 * `TenantCountryCodeResolver.Resolve` does: surrounding blanks and ONE leading `+` are removed, and a
 * value that is not a country calling code falls back to {@link PLATFORM_DEFAULT_COUNTRY_CODE}.
 *
 * Examples: `"90"` → `90`, `" +44 "` → `44`, `""` / `"abc"` / `"++1"` / `"0090"` → `90`.
 *
 * @param configured the raw setting value as it arrives in the ABP application configuration.
 * @returns a country calling code without a plus sign.
 */
export function resolveTenantCountryCode(configured: string | null | undefined): string {
  let value = configured?.trim() ?? '';
  if (value.startsWith('+')) {
    value = value.slice(1);
  }

  return COUNTRY_CODE_SHAPE.test(value) ? value : PLATFORM_DEFAULT_COUNTRY_CODE;
}
